import React from 'react'
import styled, { css, keyframes } from 'styled-components'
import palette from 'core-system/Themes/palette'
import BackgroundImage from './background.svg'
import { variant } from 'styled-system'
import LockedBadge from 'core-system/Locked/LockedBadge'
import LockedBlur from 'core-system/Locked/LockedBlur'

const shimmerKeyframe = keyframes`
  0%   { transform: translateX(-150%); }
  100% { transform: translateX(150%);  }
`

const BoxContainer = styled.div<{
  backgroundImageType?: 'gradient' | 'solid' | 'none'
  isLoading?: boolean
  overflowHidden?: boolean
}>`
  position: relative;
  border: 1px solid ${palette.grey.grey300};
  border-radius: 0.6rem;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: ${(props) => (props.overflowHidden ? 'hidden' : 'visible')};

  ${variant({
    prop: 'backgroundImageType',
    variants: {
      solid: { backgroundColor: palette.secondary.purple100 },
      gradient: { backgroundImage: `url(${BackgroundImage})` },
      none: { backgroundColor: 'transparent' },
    },
  })}
`

const BlurredContent = styled.div`
  filter: blur(12px);
  user-select: none;
  pointer-events: none;
`

const Shimmer = styled.div<{ isLoading?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    ${palette.grey.grey100} 40%,
    ${palette.grey.grey100} 60%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: ${shimmerKeyframe} 2s ease-in-out infinite;
`

const LockedBadgeContainer = styled(LockedBadge)`
  position: absolute;
  top: 12px;
  left: 12px;
`

interface BoxProps {
  children: React.ReactNode
  backgroundImageType?: 'gradient' | 'solid' | 'none'
  isLoading?: boolean
  isLocked?: boolean
  overflowHidden?: boolean
}

const Box = ({
  children,
  backgroundImageType = 'none',
  isLoading = false,
  isLocked = false,
  overflowHidden = true,
  ...props
}: BoxProps) => {
  if (isLocked) {
    return (
      <BoxContainer
        {...props}
        backgroundImageType={backgroundImageType}
        overflowHidden={overflowHidden}
      >
        <LockedBlur>{children}</LockedBlur>
        <LockedBadgeContainer />
      </BoxContainer>
    )
  }

  if (isLoading) {
    return (
      <BoxContainer
        {...props}
        backgroundImageType={backgroundImageType}
        overflowHidden={overflowHidden}
      >
        <BlurredContent>{children}</BlurredContent>
        <Shimmer />
      </BoxContainer>
    )
  }

  return (
    <BoxContainer
      {...props}
      backgroundImageType={backgroundImageType}
      overflowHidden={overflowHidden}
    >
      {children}
    </BoxContainer>
  )
}

export default Box
